import { useAppDispatch, useAppSelector } from '@/hooks'
import { referralsCollection } from '@/reducers/referrals'
import { transactionsCollection } from '@/reducers/transactions'
import { utilityProgramEnrollmentCollection } from '@/reducers/utilityProgramEnrollments'
import { RequestStatus } from '@/request/types'
import { useEffect, useState } from 'react'
import { transactionsRedemptionCollection } from '@/reducers/transactionsRedemption'
import { Transaction } from '@/types/transaction'
import { logEvent } from '@/logging'
import { programsNewCollection } from '@/reducers/programsNew'
import {
  classifyRedemptionError,
  RedemptionErrorPayload,
} from './rewards.utils'

export type ViewModelProps = ReturnType<typeof useViewModel>

export function useViewModel() {
  const DEFAULT_POINTS_TO_REDEEM = 500

  // Hooks
  const dispatch = useAppDispatch()
  const [transactionToRedeem, setTransactionToRedeem] =
    useState<Transaction | null>(null)

  // State
  const [tremendousModalOpen, setTremendousModalOpen] = useState(false)
  const [pointsRedemptionModalOpen, setPointsRedemptionModalOpen] =
    useState(false)
  const [pointsToRedeem, setPointsToRedeem] = useState<number>(
    DEFAULT_POINTS_TO_REDEEM,
  )
  const [rewardError, setRewardError] = useState<string | null>(null)
  const [pointsRedeemed, setPointsRedeemed] = useState<number | null>(null)
  const [showApprovalWarning, setShowApprovalWarning] = useState(false)

  // Fetches
  const {
    data: programOverviews,
    isLoadingOrIdle: isLoadingOrIdleProgramOverviews,
  } = programsNewCollection.useFetch()
  const { data: utilityProgramEnrollments, status } =
    utilityProgramEnrollmentCollection.useFetch()
  const { data: referrals, status: referralFetchStatus } =
    referralsCollection.useFetch()
  const { data: transactions, status: transactionFetchStatus } =
    transactionsCollection.useFetch()

  // Selectors
  const code = useAppSelector(
    (state) => state.user.user?.profile?.referral_code,
  )
  // The transactionsRedemption collection is only used for the redeem POST, so
  // its request status is the status of the in-flight redemption
  const redeemStatus = useAppSelector((state) =>
    transactionsRedemptionCollection.selectors.status(state),
  )

  // Functions
  const redeem = async () => {
    setRewardError(null)
    dispatch(
      transactionsRedemptionCollection.actions.create({
        amount: pointsToRedeem,
      }),
    ).then(
      (res: {
        payload: Transaction | RedemptionErrorPayload
        error: boolean
      }) => {
        if (res.error) {
          const { message, ambiguousOutcome } = classifyRedemptionError(
            res.payload as RedemptionErrorPayload,
          )
          // Keep the redemption modal open so the error shows inline where the
          // user confirmed, with the option to retry or back out
          setRewardError(message)
          if (ambiguousOutcome) {
            // The redemption may have completed server-side after the
            // connection was cut; refresh the activity list so the user can
            // see whether it did before retrying
            dispatch(transactionsCollection.actions.fetch())
          }
        } else {
          logEvent('rewards_redeemPoints', {
            metadata: { points: pointsToRedeem },
          })
          openTremendousModal(res.payload as Transaction, true)
          dispatch(transactionsCollection.actions.fetch())
          setPointsRedemptionModalOpen(false)
        }
      },
    )
  }

  const closeCongratsModal = () => {
    setPointsRedeemed(null)
    setPointsToRedeem(DEFAULT_POINTS_TO_REDEEM)
  }

  const completeTransaction = (transaction_id: number) => {
    if (!transactionToRedeem) return
    dispatch(
      transactionsCollection.actions.update(transaction_id, {
        status: 'completed',
      }),
    ).then((res: { payload: Transaction }) => {
      logEvent('rewards_tremendous_complete', {
        metadata: { points: Math.abs(res.payload.points) },
      })
      setPointsRedeemed(Math.abs(res.payload.points))
      dispatch(transactionsCollection.actions.fetch())
    })
  }

  const openTremendousModal = (
    transaction?: Transaction,
    showApprovalWarning?: boolean,
  ) => {
    setTremendousModalOpen(true)
    setShowApprovalWarning(showApprovalWarning ?? false)
    if (transaction) {
      setTransactionToRedeem(transaction)
    }
  }

  const closeTremendousModal = () => {
    setTremendousModalOpen(false)
    setTransactionToRedeem(null)
  }

  // Mounting Actions
  useEffect(() => {
    // invalidate fetches to ensure they get reloaded on mount
    dispatch(utilityProgramEnrollmentCollection.actions.invalidate())
    dispatch(referralsCollection.actions.invalidate())
    dispatch(transactionsCollection.actions.invalidate())
  }, [])

  useEffect(() => {
    // A stale error from a previous attempt shouldn't linger into a new one
    if (pointsRedemptionModalOpen) setRewardError(null)
  }, [pointsRedemptionModalOpen])

  // Constants
  const referralPoints =
    utilityProgramEnrollments?.[0]?.utility_program?.referral_points
  const referralLink = `https://optiwatt.com/?referral_code=${code}`
  const availablePoints = transactions?.[0]?.total_points?.points__sum || 0
  const pendingTransactions = transactions[0]?.results.filter(
    (transaction) => transaction.status.toLowerCase() === 'pending',
  )
  const pendingReferrals = referrals?.filter(
    (referral) =>
      referral.status.toLowerCase() === 'pending' &&
      referral.referred_name !== 'DELETED',
  )
  const completedTransactions = transactions[0]?.results.filter(
    (transaction) => transaction.status.toLowerCase() === 'completed',
  )
  const completedReferrals = referrals?.filter(
    (referral) => referral.status.toLowerCase() === 'completed',
  )

  const pageLoading =
    status === RequestStatus.Loading ||
    referralFetchStatus === RequestStatus.Loading ||
    transactionFetchStatus === RequestStatus.Loading ||
    isLoadingOrIdleProgramOverviews

  return {
    pageLoading,
    referralPoints,
    referralLink,
    code,
    availablePoints,
    transactions,
    pendingTransactions,
    pendingReferrals,
    completedTransactions,
    completedReferrals,
    pointsRedemptionModalOpen,
    setPointsRedemptionModalOpen,
    referralFetchStatus,
    transactionFetchStatus,
    transactionToRedeem,
    redeem,
    redeemStatus,
    rewardError,
    setRewardError,
    openTremendousModal,
    closeTremendousModal,
    pointsToRedeem,
    setPointsToRedeem,
    completeTransaction,
    tremendousModalOpen,
    pointsRedeemed,
    closeCongratsModal,
    showApprovalWarning,
    programOverviews,
  }
}
